import HotTable from "@handsontable/react";
import {
  alpha,
  Box,
  darken,
  lighten,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { registerAllModules } from "handsontable/registry";
import Papa from "papaparse";
import { useNotification } from "../../../../componentes/notificationProvider";
import { DropZone } from "../../../../componentes/newEasyAdd/dropZone";
import api from "../../../../services/api";
import Handsontable from "handsontable";
import { v4 as uuidv4 } from "uuid";
import LoadingTestsInMass from "./loadingTestsInMass";

import "handsontable/styles/ht-theme-main.css";
import "handsontable/dist/handsontable.full.min.css";

registerAllModules();

const customStyles = (theme) => `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  :root {
    --bg-app: ${theme.palette.background.card};
    --bg-card: ${theme.palette.background.card};
    --text-primary: ${theme.palette.text.primary};
    --text-secondary: ${theme.palette.text.secondary};
    --border-subtle: ${theme.palette.stroke.main};
    --row-odd: ${theme.palette.background.paper};
    --row-even: ${theme.palette.background.card};
    --row-hover: ${theme.palette.background.default};
    --header-bg: ${theme.palette.background.card};
    --header-text: ${theme.palette.text.secondary};
    --input-bg:${theme.palette.background.textField};
    --error-text: ${theme.palette.error.main};
    --header-active-bg: ${theme.palette.background.card};
    --header-active-text: ${theme.palette.text.primary};
    --action-edit: ${theme.palette.text.primary};
    --action-delete: ${theme.palette.error.main};
  }

  /* --- Ajustes Gerais e Containers --- */
  .custom-table-wrapper .handsontable,
  .custom-table-wrapper .htCore,
  .custom-table-wrapper .wtHider,
  .custom-table-wrapper .wtSpreader,
  .custom-table-wrapper .ht_clone_top,
  .custom-table-wrapper .ht_clone_left,
  .custom-table-wrapper .ht_clone_corner,
  .custom-table-wrapper .ht_master .wtHolder {
    background-color: var(--bg-app) !important;
    border: none !important;
  }

  .custom-table-wrapper { font-family: 'Inter', sans-serif; transition: background-color 0.3s ease; }
  .custom-table-wrapper .handsontable { border: none !important; overflow: hidden; }
  .ht_master .wtHider { border: none !important; color: var(--text-primary) !important; }

  /* --- Estilização de Célula de ERRO (htInvalid) --- */
  .custom-table-wrapper .handsontable td.htInvalid {
    background-color: transparent !important;
    color: var(--error-text) !important;
  }

  .custom-table-wrapper .htInvalid::after {
    display: none !important;
  }

  .cell-error-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    background-color: var(--error-text);
    color: #fff;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 900;
    z-index: 5;
    pointer-events: auto;
  }

  /* --- Cabeçalhos --- */
  .custom-table-wrapper .handsontable th {
    background-color: var(--header-bg);
    color: var(--header-text);
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid var(--border-subtle);
    border-top: none; border-right: none !important; border-left: none !important;
    padding: 0px 5px; text-align: left; vertical-align: middle;
  }

  .custom-table-wrapper .handsontable td {
    border-top: 1px solid var(--border-subtle) !important;
    border-left: none !important;
    border-right: none !important;
    border-bottom: 1px solid var(--border-subtle) !important;
    background-color: transparent !important;
    color: var(--text-primary) !important;
  }

  .error-cell { color: var(--error-text) !important; font-weight: 500; }

  .custom-table-wrapper .handsontable tbody tr:nth-of-type(odd) td,
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(odd) th { background-color: var(--row-odd) !important; }
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(even) td,
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(even) th { background-color: var(--row-even) !important; }
  .custom-table-wrapper .handsontable tbody tr:hover td,
  .custom-table-wrapper .handsontable tbody tr:hover th { background-color: var(--row-hover) !important; }

  .htDropdownMenu, .htAutocomplete { background: var(--bg-card); padding: 4px; border: 1px solid var(--border-subtle); }
  .htDropdownMenu table.htCore td, .htAutocomplete table.htCore td {
     background-color: var(--bg-card) !important; color: var(--text-primary) !important; border: none; border-radius: 4px; padding: 8px 12px;
  }

  .handsontableInput { background-color: var(--input-bg) !important; color: var(--text-primary) !important; font-family: 'Inter', sans-serif !important; font-size: 13px !important; }
  .handsontableInputHolder { background-color: var(--input-bg) !important; }

  .custom-table-wrapper .handsontable td.chip-cell {
    padding: 0 !important;
    vertical-align: middle !important;
    background-color: inherit !important; /* Herda a cor da linha (zebra) */
  }

  .chip-cell-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 100%; /* Ocupa a altura total da linha */
    min-height: 28px; /* Alinhado ao rowHeights que definimos no HotTable */
    margin: 0;
    padding: 0;
    background-color: transparent !important; 
  }
  .ht-chip {
      display: inline-flex;
      align-items: center;
      justify-content: center; height: 20px;
      padding: 0 10px;
      border-radius: 4px;
      font-size: 0.55rem;
      font-weight: 900;
      cursor: pointer; border: 1px solid transparent;
      transition: all 0.2s;
    }
    .ht-chip-success {
      background-color: ${alpha(theme.palette.sucess.main, 0.1)};
      color:  ${theme.palette.mode === "dark" ? lighten(theme.palette.sucess.main, 0.2) : darken(theme.palette.sucess.main, 0.3)};
      border-color: ${theme.palette.sucess.main};
      border-radius: 10px;
      }

    .ht-chip-error {
      background-color: ${alpha(theme.palette.error.main, 0.2)};
      color:  ${theme.palette.mode === "dark" ? lighten(theme.palette.error.main, 0.2) : darken(theme.palette.error.main, 0.3)};
      border: "0.1px solid ";
      border-color: ${theme.palette.error.main};
      border-radius: 10px;
    }
    .ht-chip-null {
      background-color:${alpha(theme.palette.chip.arquive, 0.3)};
      color:  ${theme.palette.mode === "dark" ? lighten(theme.palette.text.secondary, 0.6) : darken(theme.palette.text.secondary, 0.6)};
      border-color: var(--text-secondary);
      border-radius: 10px;
      opacity: 0.7;
    }
    .ht-chip-null:hover { opacity: 1; border-color: var(--text-secondary); }

    .ht-loader-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid ${theme.palette.mode === "dark" ? "#444" : "#eee"};
    border-top: 2px solid ${theme.palette.primary.main};
    border-radius: 50%;
    display: inline-block;
    animation: ht-spin 0.8s linear infinite;
    vertical-align: middle;
  }

  @keyframes ht-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }


  .action-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 50%;
      transition: background-color 0.2s;
  }
  .action-btn:hover { background-color: rgba(0,0,0,0.05); }
  .action-btn.edit { color: var(--action-edit); }
  .action-btn.delete { color: var(--action-delete); }

  .custom-table-wrapper .handsontable th.ht__highlight {
    background-color: var(--header-active-bg) !important;
    color: var(--header-active-text) !important;
    border-bottom: 2px solid var(--header-active-text);
    transition: background-color 0.2s ease;
  }

  .custom-table-wrapper.view-mode .htAutocompleteArrow { display: none !important; }
  .custom-table-wrapper.view-mode .htAutocomplete { padding-right: 5px !important; }
  .custom-table-wrapper input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border: 2px solid;
    border-radius: 3px;
    background-color: transparent;
    cursor: pointer;
    position: relative;
    border-color: ${theme.palette.text.main}80;
}
.custom-table-wrapper input[type="checkbox"]:hover {
  border-color: var(--header-active-text);
  background-color: ${alpha(theme.palette.primary.main, 0.08)};
}
  .custom-table-wrapper input[type="checkbox"]:checked {
  background-color: var(--header-active-text);
  border-color: var(--header-active-text);
}
.custom-table-wrapper input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  top: 1px;
  left: 4px;
  width: 4px;
  height: 8px;
  border: solid ${theme.palette.background.paper};
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.custom-table-wrapper .handsontable,
.custom-table-wrapper .handsontable thead,
.custom-table-wrapper .handsontable thead tr:first-child th {
  border-top: none !important;
}
`;

const ipRegex =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;

const regexFinalString =
  /(?:\b(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}|\b(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|\b(?:[0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|\b:(?::[0-9a-fA-F]{1,4}){1,7}|::)$/;

export const HostsReview = forwardRef((props, ref) => {
  const {
    csvData,
    setCsvData,
    onValidationStatusChange,
    setIsTesting,
    isTesting,
    onSaveHosts,
    templates,
    proxys,
  } = props;

  const [isDragging, setIsDragging] = useState(false);
  const [internalData, setInternalData] = useState([]);
  const [processedHosts, setProcessedHosts] = useState(0);

  const hotRef = useRef(null);
  const notify = useNotification();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add("dark-theme");
      document.body.style.backgroundColor = theme.palette.background.default;
    } else {
      document.body.classList.remove("dark-theme");
      document.body.style.backgroundColor = theme.palette.background.default;
    }
    document.body.style.backgroundColor = theme.palette.background.default;
  }, [isDarkMode, theme]);

  const { proxyNames, proxyMap } = useMemo(() => {
    const names = [];
    const map = {};
    if (Array.isArray(proxys)) {
      proxys.forEach((p) => {
        names.push(p.host);
        map[p.proxyid] = { host: p.host, description: p.description };
      });
    }
    return { proxyNames: names, proxyMap: map };
  }, [proxys]);

  const { manufacturerList, equipmentMap } = useMemo(() => {
    const map = {};
    const list = [];
    if (Array.isArray(templates)) {
      templates.forEach((item) => {
        const key = item?.fabricante;
        if (key) {
          list.push(key);
          map[key] = item?.Funcoes.flatMap((f) =>
            f.Modelos.map((m) => ({
              id: m.id,
              label: `${f.funcao} - ${m.modelo}`,
            })),
          );
        }
      });
    }
    return { manufacturerList: list, equipmentMap: map };
  }, [templates]);

  const validateRow = useCallback(
    (row) => {
      const errors = {};

      if (!row.Nome?.trim()) errors.Nome = "Obrigatório";

      if (!row.IP?.trim()) {
        errors.IP = "Obrigatório";
      } else if (!ipRegex.test(row.IP)) {
        errors.IP = "IP inválido";
      }

      if (!row.Fabricante) {
        errors.Fabricante = "Obrigatório";
      } else if (
        manufacturerList.length > 0 &&
        !manufacturerList.includes(row.Fabricante)
      ) {
        errors.Fabricante = "Fabricante não reconhecido";
      }

      if (!row.Modelo) {
        errors.Modelo = "Obrigatório";
      } else {
        const validModels = (equipmentMap[row.Fabricante] || []).map(
          (e) => e.label,
        );
        if (validModels.length > 0 && !validModels.includes(row.Modelo)) {
          errors.Modelo = "Modelo inválido para este fabricante";
        }
      }

      const validInterfaces = ["SNMPv1", "SNMPv2", "AGENTE"];
      if (!row["Tipo interface"]) {
        errors["Tipo interface"] = "Obrigatório";
      } else if (!validInterfaces.includes(row["Tipo interface"])) {
        errors["Tipo interface"] = "Opção inválida";
      }

      const isSnmp = row["Tipo interface"]?.startsWith("SNMP");
      if (isSnmp && !row["Comunidade SNMP"]?.trim()) {
        errors["Comunidade SNMP"] = "Obrigatório para SNMP";
      }

      const validAccess = ["SSH", "Telnet"];
      if (row["Tipo Acesso"] && !validAccess.includes(row["Tipo Acesso"])) {
        errors["Tipo Acesso"] = "Opção inválida";
      }

      const hasAccessInfo =
        row["User Acesso"] || row["Senha Acesso"] || row["Porta Acesso"];
      if (hasAccessInfo) {
        if (!row["User Acesso"]?.trim()) errors["User Acesso"] = "Obrigatório";
        if (!row["Senha Acesso"]?.trim())
          errors["Senha Acesso"] = "Obrigatório";
        if (!row["Porta Acesso"]) errors["Porta Acesso"] = "Obrigatório";
      }

      if (
        row.Proxy &&
        proxyNames.length > 0 &&
        !proxyNames.includes(row.Proxy)
      ) {
        errors.Proxy = "Proxy não encontrado";
      }

      return errors;
    },
    [manufacturerList, equipmentMap, proxyNames],
  );

  useEffect(() => {
    if (csvData && csvData.length > 0) {
      const initializedData = csvData.map((row) => {
        const proxyDisplayName = proxyMap[row.Proxy]?.host || row.Proxy;
        const newRow = {
          ...row,
          Proxy: proxyDisplayName,
          Status: row.Status || {
            ping: null,
            snmp: null,
            ssh: null,
            telnet: null,
          },
        };
        newRow._errors = validateRow(newRow);
        return newRow;
      });

      setInternalData(initializedData);
      const hasErrors = initializedData.some(
        (r) => Object.keys(r._errors).length > 0,
      );
      onValidationStatusChange?.(hasErrors);
    }
  }, [csvData, proxyMap, validateRow]);

  const errorCellRenderer = function (
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  ) {
    const isDropdown = cellProperties.type === "dropdown";

    if (isDropdown) {
      Handsontable.renderers.DropdownRenderer.apply(this, arguments);
    } else {
      Handsontable.renderers.TextRenderer.apply(this, arguments);
    }

    const rowData = instance.getSourceDataAtRow(row);
    const errorMessage = rowData?._errors?.[prop];

    const oldIcon = td.querySelector(".cell-error-icon");
    if (oldIcon) oldIcon.remove();
    td.classList.remove("htInvalid");
    td.title = "";

    if (errorMessage) {
      td.classList.add("htInvalid");

      if (!isDropdown) {
        td.style.position = "relative";
        const errorIcon = document.createElement("span");
        errorIcon.className = "cell-error-icon";
        errorIcon.textContent = "!";
        td.appendChild(errorIcon);
      }

      td.title = errorMessage;
    }
  };

  const chipRenderer = (
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  ) => {
    while (td.firstChild) {
      td.removeChild(td.firstChild);
    }

    const rowData = instance.getSourceDataAtRow(row);

    if (rowData?._loading) {
      const loader = document.createElement("div");

      loader.className = "ht-loader-spinner";

      td.appendChild(loader);

      td.style.textAlign = "center";

      return;
    }

    const tipoAcesso = instance.getDataAtRowProp(row, "Tipo Acesso");
    const tipoInterface = instance.getDataAtRowProp(row, "Tipo interface");

    if (!value || typeof value !== "object") {
      td.innerText = "";
      td.className = "chip-cell";
      return;
    }

    let checks = [{ key: "ping", label: "PING" }];

    if (tipoInterface?.startsWith("SNMP")) {
      checks.push({ key: "snmp", label: "SNMP" });
    }
    if (tipoAcesso === "SSH") {
      checks.push({ key: "ssh", label: "SSH" });
    } else if (tipoAcesso === "Telnet") {
      checks.push({ key: "telnet", label: "TELNET" });
    }

    const container = document.createElement("div");

    container.className = "chip-cell-container";

    checks.forEach((check) => {
      const checkValue = value[check.key];

      const span = document.createElement("span");

      let chipClass = "ht-chip-null";

      if (checkValue === true) {
        chipClass = "ht-chip-success";
      } else if (checkValue === false) {
        chipClass = "ht-chip-error";
      }

      span.className = `ht-chip ${chipClass}`;
      span.innerText = check.label;
      span.dataset.action = "toggle-check";
      span.dataset.check = check.key;
      span.dataset.row = row;

      container.appendChild(span);
    });

    td.appendChild(container);

    td.innerHTML = "";

    td.appendChild(container);

    td.classList.add("chip-cell");
  };

  const columns = useMemo(
    () => [
      { data: "Nome", title: "Nome", renderer: errorCellRenderer, width: 100 },
      { data: "IP", title: "IP", renderer: errorCellRenderer, width: 100 },
      {
        data: "Fabricante",
        title: "Fabricante",
        type: "dropdown",
        source: manufacturerList,
        renderer: errorCellRenderer,
      },
      {
        data: "Modelo",
        title: "Modelo",
        type: "dropdown",
        source: [],
        renderer: errorCellRenderer,
      },
      {
        data: "Tipo interface",
        title: "Tipo Interface",
        type: "dropdown",
        source: ["SNMPv1", "SNMPv2", "AGENTE"],
        renderer: errorCellRenderer,
      },
      {
        data: "Porta Interface",
        title: "Porta Interface",
        renderer: errorCellRenderer,
        width: 90,
      },
      {
        data: "Comunidade SNMP",
        title: "Comunidade SNMP",
        renderer: errorCellRenderer,
      },
      {
        data: "Tipo Acesso",
        title: "Tipo Acesso",
        type: "dropdown",
        source: ["SSH", "Telnet"],
        renderer: errorCellRenderer,
        width: 90,
      },
      {
        data: "User Acesso",
        title: "User Acesso",
        renderer: errorCellRenderer,
        width: 100,
      },
      {
        data: "Senha Acesso",
        title: "Senha Acesso",
        renderer: errorCellRenderer,
        width: 80,
      },
      {
        data: "Porta Acesso",
        title: "Porta Acesso",
        renderer: errorCellRenderer,
        width: 100,
      },
      {
        data: "Proxy",
        title: "Proxy",
        type: "dropdown",
        source: proxyNames,
        renderer: errorCellRenderer,
        width: 130,
      },
      {
        data: "Status",
        title: "Status",
        readOnly: true,
        width: 150,
        renderer: chipRenderer,
      },
    ],
    [manufacturerList, proxyNames],
  );

  const handleAfterChange = useCallback(
    (changes, source) => {
      if (!changes || source === "loadData" || source === "internal_update")
        return;

      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      changes.forEach(([row, prop, oldValue, newValue]) => {
        if (oldValue === newValue) return;

        const rowData = hot.getSourceDataAtRow(row);
        if (prop === "Fabricante") {
          hot.setDataAtRowProp(row, "Modelo", null, "internal_update");
        }

        rowData._errors = validateRow(rowData);
      });

      hot.render();
      const updatedData = hot.getSourceData();
      setCsvData([...updatedData]);
      const hasErrors = updatedData.some(
        (r) => Object.keys(r._errors).length > 0,
      );
      onValidationStatusChange?.(hasErrors);
    },
    [setCsvData, validateRow, onValidationStatusChange],
  );

  const cellsConfig = useCallback(
    (row, col, prop) => {
      const cellProperties = {};
      const hotInstance = hotRef.current?.hotInstance;

      if (hotInstance && prop === "Modelo") {
        const fabricante = hotInstance.getDataAtRowProp(row, "Fabricante");
        cellProperties.source = (equipmentMap[fabricante] || []).map(
          (e) => e.label,
        );
      }
      return cellProperties;
    },
    [equipmentMap],
  );

  const processTestsInChunks = useCallback(async (dataToProcess) => {
    if (!dataToProcess || dataToProcess.length === 0) return;

    const totalHosts = dataToProcess.length;
    const getChunkSize = (total) => {
      if (total <= 5) return 1;
      if (total <= 20) return 5;

      return 10;
    };

    const chunkSize = getChunkSize(totalHosts);
    setProcessedHosts(0);
    setIsTesting(true);
    let currentData = [...dataToProcess];

    for (let i = 0; i < totalHosts; i += chunkSize) {
      const chunk = currentData.slice(i, i + chunkSize);

      chunk.forEach((host) => {
        host.Status = { ping: null, snmp: null, ssh: null };
        host._loading = true;
      });

      setInternalData([...currentData]);

      try {
        const response = await api.post("easyAdd/zabbix/massTesting", {
          hosts: chunk,
        });

        if (response.data.error) {
          const error = response.data.error;
          notify(
            `Erro ao executar testes em massa: ${error.code} - ${error.data}`,
            "error",
            5000,
          );
          setIsTesting(false);
          return;
        }

        response?.data.forEach((res) => {
          const rowIndex = currentData.findIndex((h) => h.id === res.id);
          if (rowIndex !== -1) {
            currentData[rowIndex] = {
              ...currentData[rowIndex],
              Status: res.details,
              _loading: false,
            };
          }
        });
        const nextProcessed = Math.min(i + chunkSize, totalHosts);
        setProcessedHosts(nextProcessed);
        await new Promise((resolve) => setTimeout(resolve, 30));
      } catch (error) {
        notify("Falha ao processar lote. Verifique a conexão.", "error");
      }

      setInternalData([...currentData]);
    }

    setIsTesting(false);
    notify("Todos os testes foram concluídos!", "success");
  }, []);

  const handleAddHostsInMass = useCallback(
    (hosts) => {
      const processedHosts = [];

      hosts.forEach((row, index) => {
        let selectedTemplates = [];
        let selectedMacros = [];
        let selectedGroups = [];
        let modeloData = null;
        let funcaoData = null;

        const hasUserSsh = !!row["User Acesso"]?.trim();

        const fabricanteData = templates.find(
          (item) =>
            item.fabricante.toLowerCase() === row.Fabricante.toLowerCase(),
        );

        if (
          fabricanteData &&
          row.Modelo &&
          Array.isArray(fabricanteData?.Funcoes)
        ) {
          for (const funcao of fabricanteData.Funcoes) {
            const modelo = funcao.Modelos?.find(
              (m) =>
                m.id ===
                equipmentMap[row.Fabricante]?.find(
                  (e) => e.label === row.Modelo,
                )?.id,
            );

            if (modelo) {
              funcaoData = funcao;
              modeloData = modelo;
              break;
            }
          }
          const predefinicoes = modeloData?.Predefinicoes;

          if (predefinicoes) {
            try {
              selectedTemplates = predefinicoes.templates
                ? JSON.parse(predefinicoes.templates)
                : [];
              selectedMacros = predefinicoes.macros
                ? JSON.parse(predefinicoes.macros)
                : [];
              selectedGroups = predefinicoes.groups
                ? JSON.parse(predefinicoes.groups)
                : [];
            } catch (error) {
              selectedTemplates = [];
              selectedMacros = [];
              selectedGroups = [];
            }
          }
        }

        const nameIp = regexFinalString.test(row.Nome?.trim());
        const modeloName = modeloData?.modelo || row.Modelo;
        const funcaoName = funcaoData?.funcao || null;

        const proxyEntry = proxys.find(
          (p) => p.proxyid === row.Proxy || p.host === row.Proxy,
        );

        const finalObject = {
          nome: nameIp
            ? row.Nome?.trim()
            : `${row.Nome?.trim()} - ${row.IP?.trim()}`,
          ip: row.IP.trim(),
          ...(row["Tipo interface"] !== "AGENTE" && {
            comunidadeSnmp: row["Comunidade SNMP"]?.trim(),
            userAcesso: row["User Acesso"]?.trim() || null,
            senhaAcesso: hasUserSsh ? row["Senha Acesso"]?.trim() : null,
            portaAcesso: hasUserSsh ? Number(row["Porta Acesso"]) : null,
            tipoAcesso: row["Tipo Acesso"] === "SSH" ? 0 : 1,
          }),
          portaSNMP: Number(row["Porta Interface"]),
          fabricante: row.Fabricante,
          modelo: modeloName,
          funcao: funcaoName,
          proxy: proxyEntry?.host || row.Proxy,
          groups: selectedGroups,
          templates: selectedTemplates,
          macros: selectedMacros,
          tipoInterface: row["Tipo interface"],
          id: row.id,
        };

        processedHosts.push({
          ...finalObject,
        });
      });

      onSaveHosts(processedHosts);
    },
    [proxys, templates, equipmentMap],
  );

  useImperativeHandle(ref, () => ({
    processMassTests() {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      const dataToTest = hot.getSourceData().map((row) => {
        const proxyEntry = proxys.find((p) => p.host === row.Proxy);
        return {
          ...row,
          proxy: proxyEntry?.host || row.Proxy,
          machineId: proxyEntry?.description || "",
          _loading: true,
        };
      });
      return processTestsInChunks(dataToTest);
    },
    processAddHostsInMass(onlySuccessful = false) {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      let data = hot.getSourceData();
      if (onlySuccessful) {
        data = data.filter((row) => {
          const s = row.Status || {};
          const isSnmp = row["Tipo interface"]?.startsWith("SNMP");
          const snmpOk = isSnmp ? s.snmp === true : true;
          const isAcc = !!row["Tipo Acesso"];
          const accOk = isAcc ? s.ssh === true || s.telnet === true : true;
          return s.ping === true && snmpOk && accOk;
        });
      }
      return handleAddHostsInMass(data);
    },
    getRawTableData() {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return [];

      return hot.getSourceData();
    },
  }));

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e) => {
    handleFiles(e);
  };

  const handleFiles = (e) => {
    const file = e.target.files[0];

    if (!file) return;
    const isCsv =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

    if (!isCsv) {
      notify("Apenas arquivos .csv são permitidos.", "error");
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      encoding: "UTF-8",
      complete: (results) => {
        const data = results.data.map((row) => ({
          ...row,
          _errors: {},
          Status: { ping: null, snmp: null, ssh: null, telnet: null },
          id: uuidv4(),
        }));
        setCsvData(data);
        e.target.value = null;
      },
      error: (error) => {
        notify("Erro ao ler o arquivo CSV", "error");
      },
    });
  };
  return (
    <Paper
      sx={{
        borderRadius: "16px",
        backgroundColor: theme.palette.background.card,
      }}
    >
      <style>{customStyles(theme, isDarkMode)}</style>
      <Box sx={{ minHeight: "400px" }}>
        {csvData.length === 0 ? (
          <DropZone
            isDragging={isDragging}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleFileInput={handleFileInput}
            theme={theme}
          />
        ) : (
          <>
            <Stack
              sx={{
                padding: 2,
                borderBottom: `2px solid ${theme.palette.stroke.main}`,
              }}
            >
              <Typography variant="subtitle1">Hosts Importados</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {internalData.length} host(s) encontrado(s)
              </Typography>
            </Stack>
            {isTesting ? (
              <LoadingTestsInMass
                processedHosts={processedHosts}
                internalData={internalData}
              />
            ) : (
              <HotTable
                ref={hotRef}
                data={internalData}
                licenseKey="non-commercial-and-evaluation"
                rowHeaders={false}
                height="35vh"
                stretchH="all"
                rowHeights={28}
                columnSorting={true}
                cells={cellsConfig}
                columns={columns}
                afterChange={handleAfterChange}
                manualColumnResize={true}
                className="custom-table-wrapper"
              />
            )}
          </>
        )}
      </Box>
    </Paper>
  );
});
